import ServiceAccounts from "@affinity/Store/ServiceAccounts";
import { createHandlerChainRunner, Handler, HasOnComplete, HasOnError } from "@insite/client-framework/HandlerCreator";
import { getCart } from "@insite/client-framework/Services/CartService";
import { getBillTo } from "@insite/client-framework/Services/CustomersService";
import { BillToModel, CartModel, OrderModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";




// External properties to call chain
export interface OrderCompleteInitialiseParameter {
    billToId: string;
    cartId: string;
}

export interface OrderCompleteInitialseResults {
    cart: CartModel | undefined;
    billTo: BillToModel;
    quantity: number;
    delivery: Date | undefined;
    address: ShipToModel | undefined;
    hasError: boolean;
    errorMessage: string;
}

type Parameter = OrderCompleteInitialiseParameter & HasOnComplete<OrderCompleteInitialseResults> & HasOnError<OrderCompleteInitialseResults>
type HandlerType = Handler<Parameter, OrderCompleteInitialseResults>;

const LoadBillTo: HandlerType = async props => {
    if(!props.parameter.billToId) {
        props.hasError = true;
        props.errorMessage = "Parameter: BillToId is Required";
        return;
    }

    try {
        props.billTo = await getBillTo({ billToId: props.parameter.billToId });
    }
    catch(error) {
        props.hasError = true;
        props.errorMessage = error.toString();
    }
}

const LoadOrder: HandlerType = async props => {
    if(props.hasError) {
        return;
    }

    if(!props.parameter.cartId) {
        props.hasError = true;
        props.errorMessage = "Parameter: OrderId is Required";
        return;
    }

    try {
        const result = await getCart({ cartId: props.parameter.cartId, expand: [ "cartLines", "shipTos" ] });
        props.cart = result?.cart;
        props.address = result?.shipTo;
    } catch(error) {
        props.hasError = true;
        props.errorMessage = error.toString();
    }
}

const GetProperties: HandlerType = props => {
    if(props.hasError) {
        return;
    }

    const cartLine = props.cart?.cartLines?.[0];
    if(cartLine == null) {
        props.hasError = true;
        props.errorMessage = "Order not found";
        return;
    }

    props.quantity = cartLine.qtyOrdered ?? 0;
    const serviceHelper = new ServiceAccounts();
    const pricingInfo = serviceHelper.getHarrierPricingInfo(cartLine);
    const shipToErp = serviceHelper.getOrdersShipTo(cartLine);
    props.delivery = pricingInfo?.harrierSource?.deliveryBy;
}

const TriggerOnComplete: HandlerType = props => {
    if (props.hasError) {
        return;
    }

    props.parameter.onComplete?.(props);
}

const TriggerOnError: HandlerType = props => {
    if (!props.hasError) {
        return;
    }

    props.parameter.onError?.(props);
}

const chain = [
    LoadBillTo,
    LoadOrder,
    GetProperties,
    TriggerOnComplete,
    TriggerOnError,
];

const orderCompleteInitialise = createHandlerChainRunner(chain, "OrderCompleteInitialise");
export default orderCompleteInitialise;